import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { baseURL } from "../../config";
import setUpAxios from "../../setUpAxios";
import { getCachedUsers, setCachedUsers } from "../../utils/userCache.jsx";
import { useTranslation } from "react-i18next";

const ChevronIcon = ({ open }) => (
  <svg
    viewBox="0 0 24 24"
    className={`w-5 h-5 text-primary transition-transform duration-200 ${open ? "rotate-180" : ""}`}
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const SearchIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="w-4 h-4 text-[#6B7280]"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </svg>
);

// New icon for the empty state to draw attention
const EmptyUserIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="w-5 h-5 text-primary"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const Avatar = ({ name, src, size = "h-10 w-10" }) => {
  if (src) {
    return (
      <img
        className={`${size} rounded-full object-cover flex-shrink-0 border-[1.5px] border-paper`}
        src={src}
        alt=""
      />
    );
  }
  return (
    <div
      className={`${size} rounded-full bg-paper border border-dashed border-line text-primary flex items-center justify-center font-slab font-bold text-lg flex-shrink-0`}
    >
      {name?.[0]?.toUpperCase() || "?"}
    </div>
  );
};

const UserList = ({
  required = true,
  onSelectUser,
  selectedUser,
  onUserChange,
  onDataLoaded,
  onLoadError,
}) => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [visible, setVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState("");

  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const cached = getCachedUsers();
    if (cached) {
      setUsers(cached);
      setIsLoading(false);
      if (onDataLoaded) onDataLoaded(cached);
    } else {
      setUpAxios();
      axios
        .get(`${baseURL}/users`)
        .then((response) => {
          if (Array.isArray(response.data)) {
            setUsers(response.data);
            setCachedUsers(response.data);
            if (onDataLoaded) onDataLoaded(response.data);
          } else {
            console.error("Unexpected response format:", response.data);
            if (onLoadError) onLoadError(new Error("Unexpected response format"));
          }
        })
        .catch((error) => {
          console.error("Error fetching users:", error);
          if (onLoadError) onLoadError(error);
        })
        .finally(() => setIsLoading(false));
    }
  }, []);

  useEffect(() => {
    if (selectedUser && users.length > 0) {
      const foundUser = users.find((user) => user.username === selectedUser);
      if (foundUser) {
        setSelectedUserId(foundUser.id.toString());
        onSelectUser(foundUser.id.toString(), foundUser);
        if (onUserChange) {
          onUserChange(foundUser.name.toString());
        }
      }
    }
  }, [selectedUser, users]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (visible) {
      setQuery("");
      setTimeout(() => searchRef.current?.focus(), 50);
      const handleEscape = (e) => e.key === "Escape" && setVisible(false);
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [visible]);

  const handleUserSelect = (user) => {
    setSelectedUserId(user.id.toString());
    onSelectUser(user.id.toString(), user);
    if (onUserChange) {
      onUserChange(user.name);
    }
    setVisible(false);
  };

  const selectedUserObj = users.find(
    (user) => user.id === parseInt(selectedUserId),
  );

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.username?.toLowerCase().includes(q),
    );
  }, [users, query]);

  return (
    <div ref={containerRef} className="relative w-full">
      <label className="mb-2 ml-0.5 block text-[12px] font-bold text-ink">
        {t("userList.label")}{" "}
        {required && <span className="text-debit ml-0.5">*</span>}
      </label>

      <div
        onClick={() => setVisible(!visible)}
        className={`flex min-h-[62px] cursor-pointer items-center justify-between rounded-2xl border bg-white px-4 py-3 transition duration-200 ${
          visible
            ? "border-primary ring-4 ring-primary/10"
            : "border-line shadow-[0_2px_8px_rgba(20,35,58,0.04)] hover:border-primary/50"
        }`}
      >
        {isLoading ? (
          <div className="flex items-center space-x-3 py-0.5 w-full">
            <div className="h-10 w-10 rounded-full bg-paper border border-line animate-pulse" />
            <div className="space-y-2 flex-1">
              <div className="h-3.5 w-32 rounded bg-line/60 animate-pulse" />
              <div className="h-2.5 w-20 rounded bg-line/40 animate-pulse" />
            </div>
          </div>
        ) : selectedUserId && selectedUserObj ? (
          <div className="flex items-center space-x-3 min-w-0">
            <Avatar
              name={selectedUserObj.name}
              src={selectedUserObj.profile_img}
            />
            <div className="flex flex-col min-w-0">
              <p className="font-bold text-primary text-[14.5px] truncate capitalize leading-tight mb-0.5">
                {selectedUserObj.name}
              </p>
              <p className="text-[11.5px] font-medium text-[#6B7280] truncate leading-none">
                {selectedUserObj.username}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 py-1">
            <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
              <EmptyUserIcon />
            </div>
            <p className="text-primary font-bold text-[14px]">
              {t("userList.selectPrompt")}
            </p>
          </div>
        )}
        <ChevronIcon open={visible} />
      </div>

      {visible && (
        <div className="absolute left-0 top-full z-[120] mt-2 flex w-full flex-col overflow-hidden rounded-2xl border border-line bg-white shadow-[0_18px_42px_rgba(20,35,58,0.16)]">
          <div className="p-3 border-b border-line bg-paper/30">
            <div className="flex items-center gap-2 px-3 py-2.5 bg-white border border-line focus-within:border-primary focus-within:ring-1 focus-within:ring-primary rounded-lg transition-all">
              <SearchIcon />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("userList.searchPlaceholder")}
                className="w-full bg-transparent text-[13.5px] font-medium text-primary placeholder:text-[#6B7280] outline-none"
              />
            </div>
          </div>

          <ul className="divide-y divide-line/50 max-h-64 overflow-y-auto">
            {filteredUsers.length === 0 ? (
              <li className="px-4 py-8 text-center flex flex-col items-center gap-2">
                <SearchIcon />
                <span className="text-[13px] font-bold text-primary">
                  {t("userList.noCustomers")}
                </span>
                <span className="text-[11px] font-medium text-[#6B7280]">
                  {t("userList.trySearch")}
                </span>
              </li>
            ) : (
              filteredUsers.map((user) => {
                const isSelected = user.id === parseInt(selectedUserId);
                return (
                  <li
                    key={user.id}
                    onClick={() => handleUserSelect(user)}
                    className={`flex items-center px-4 py-3 cursor-pointer transition-colors ${
                      isSelected ? "bg-primary/5" : "hover:bg-amber/10"
                    }`}
                  >
                    <Avatar
                      name={user.name}
                      src={user.profile_img}
                      size="h-9 w-9"
                    />
                    <div className="ml-3 min-w-0 flex-1">
                      <p className="font-bold text-primary text-[13.5px] truncate capitalize mb-0.5">
                        {user.name}
                      </p>
                      <p className="text-[11px] font-medium text-[#6B7280] truncate leading-none">
                        {user.username}
                      </p>
                    </div>
                    {isSelected && (
                      <svg
                        viewBox="0 0 24 24"
                        className="w-5 h-5 text-primary flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default UserList;
